import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import {
  getStudioUnreadCount,
  listStudioNotifications,
  markAllStudioNotificationsRead,
  markStudioNotificationRead,
  StudioInboxItem,
} from "../services/studioInboxApi";

const POLL_MS = 45000;

const relativeTime = (value?: string | number | Date): string => {
  if (value == null || value === "") {
    return "";
  }
  const ms = typeof value === "number" ? value : new Date(value).getTime();
  if (!Number.isFinite(ms)) {
    return "";
  }
  const delta = Date.now() - ms;
  if (delta < 45 * 1000) {
    return "Just now";
  }
  if (delta < 60 * 60 * 1000) {
    const mins = Math.max(1, Math.round(delta / 60000));
    return mins + " min ago";
  }
  if (delta < 24 * 60 * 60 * 1000) {
    const hours = Math.max(1, Math.round(delta / 3600000));
    return hours + "h ago";
  }
  const days = Math.max(1, Math.round(delta / 86400000));
  return days + "d ago";
};

const StudioNotificationBell = () => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<StudioInboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshUnread = useCallback(async () => {
    const res: any = await getStudioUnreadCount();
    if (res?.success) {
      setUnread(Number(res?.data?.unreadCount || 0));
      return;
    }
    if (res?.message === "FEATURE_DISABLED") {
      setUnread(0);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    const res: any = await listStudioNotifications(0, 20);
    setLoading(false);
    if (!res?.success) {
      setError(res?.message || "Could not load notifications.");
      setItems([]);
      return;
    }
    const nextItems = Array.isArray(res?.data?.items) ? res.data.items : [];
    setItems(nextItems);
    setUnread(Number(res?.data?.unreadCount || 0));
  }, []);

  useEffect(() => {
    refreshUnread();
    const timer = window.setInterval(refreshUnread, POLL_MS);
    const onFocus = () => refreshUnread();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshUnread]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      loadList();
    }
  };

  const openItem = async (item: StudioInboxItem) => {
    if (item?.id && !item.read) {
      const res: any = await markStudioNotificationRead(item.id);
      if (res?.success) {
        setUnread(Number(res?.data?.unreadCount || 0));
        setItems((current) =>
          current.map((row) => (row.id === item.id ? { ...row, read: true } : row))
        );
      }
    }
    setOpen(false);
    const path = item?.actionPath || "/products";
    history.push(path);
  };

  const markAll = async () => {
    const res: any = await markAllStudioNotificationsRead();
    if (!res?.success) {
      setError(res?.message || "Could not mark notifications as read.");
      return;
    }
    setUnread(0);
    setItems((current) => current.map((row) => ({ ...row, read: true })));
  };

  const badge = unread > 9 ? "9+" : String(unread);

  return (
    <Dropdown isOpen={open} toggle={toggle} className="studio-notify-bell">
      <DropdownToggle
        caret={false}
        className="studio-notify-bell__toggle"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.73 21a2 2 0 0 1-3.46 0"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unread > 0 ? (
          <span className="studio-notify-bell__badge">{badge}</span>
        ) : null}
      </DropdownToggle>
      <DropdownMenu end className="studio-notify-bell__menu">
        <div className="studio-notify-bell__head">
          <span>Notifications</span>
          {unread > 0 ? (
            <button
              type="button"
              className="studio-notify-bell__mark"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                markAll();
              }}
            >
              Mark all read
            </button>
          ) : null}
        </div>
        {loading ? (
          <div className="studio-notify-bell__state">Loading notifications…</div>
        ) : error ? (
          <div className="studio-notify-bell__state">{error}</div>
        ) : items.length === 0 ? (
          <div className="studio-notify-bell__state">No notifications yet.</div>
        ) : (
          items.map((item) => (
            <DropdownItem
              key={item.id}
              className={`studio-notify-bell__item${item.read ? "" : " is-unread"}`}
              onClick={() => openItem(item)}
            >
              <span className="studio-notify-bell__title">{item.title || "Update"}</span>
              <span className="studio-notify-bell__body">{item.body || ""}</span>
              <span className="studio-notify-bell__time">{relativeTime(item.createdAt)}</span>
            </DropdownItem>
          ))
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export default StudioNotificationBell;
export { relativeTime };
